"use client";

import { useState } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

interface ImagePopupProps {
  src: string;
  alt: string;
  onClose: () => void;
}

const ImagePopup = ({ src, alt, onClose }: ImagePopupProps) => {
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-fadeIn"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all hover:scale-110"
        aria-label="Close popup"
      >
        <Icon name="XMarkIcon" size={32} />
      </button>
      <div 
        className="relative max-w-6xl max-h-[90vh] w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <AppImage
          src={src}
          alt={alt}
          className="w-full h-full object-contain rounded-lg"
        />
      </div>
    </div>
  );
};

const ProjectDetailsSection = () => {
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [popupImage, setPopupImage] = useState<{ src: string; alt: string } | null>(null);

  const projects = [
  {
    id: "swastikapartment",
    slug: "/projects/swastik-apartment",
    title: "Swastik Apartment",
    location: "NH-12 Diamond Harbour Near Pailan Market, Chak Rajmolla, West Bengal 700104",
    type: "Residential Apartment",
    status: "New Launch",
    description: "Introducing Swastik Apartment — a premium residential project located on NH-12 Diamond Harbour Road near Pailan Market, Chak Rajmolla. Enjoy modern living with excellent connectivity, close proximity to markets, schools, and essential amenities in a rapidly developing locality of West Bengal.",
    images: [
    {
      id: "img_swastik_1",
      src: "/assets/images/cover-1773997396852.jpg",
      alt: "Swastik Apartment building exterior view at NH-12 Diamond Harbour near Pailan Market, Chak Rajmolla, West Bengal"
    },
    {
      id: "img_swastik_2",
      src: "/assets/images/1-1773999995664.jpg",
      alt: "Swastik Apartment interior or site view at NH-12 Diamond Harbour near Pailan Market, Chak Rajmolla, West Bengal"
    },
    {
      id: "img_swastik_3",
      src: "/assets/images/Price_Trend-1774007396207.png",
      alt: "Swastik Apartment price trend chart showing property price growth in the area"
    }],

    features: [
    "Prime location on NH-12 Diamond Harbour Road",
    "Near Pailan Market for daily conveniences",
    "Excellent road connectivity",
    "Developing residential locality",
    "Clear property documentation"],

    highlights: [
    { id: "highlight_swastik_size", icon: "MapIcon", label: "Project Type", value: "Apartment" },
    { id: "highlight_swastik_price", icon: "CurrencyRupeeIcon", label: "Price", value: "Rs.3600/sqft" },
    { id: "highlight_swastik_location", icon: "MapPinIcon", label: "Location", value: "Chak Rajmolla, WB" }]

  },
  {
    id: "sonarbangla",
    slug: "/projects/sonarbangla",
    title: "Sonarbangla",
    location: "Joka, Kolkata – 700104",
    type: "Land for Sale",
    status: "Available Now",
    description: "Prime residential plot in the Sonarbangla area of Joka. Excellent connectivity to Kolkata city center, close to schools, hospitals, and markets. Clear documentation and ready for construction.",
    images: [
    {
      id: "img_1",
      src: "/assets/images/444701890O-1767622133417-1769965986174.jpg",
      alt: "Sonarbangla project site view"
    },
    {
      id: "img_2",
      src: "/assets/images/Sonarbangla-1769966408949.jpg",
      alt: "Sonarbangla project site view"
    }],

    features: [
    "Clear property documentation",
    "Excellent road connectivity",
    "Nearby Joka Metro station",
    "Developing residential area",
    "Schools and hospitals nearby"],

    highlights: [
    { id: "highlight_size", icon: "MapIcon", label: "Plot Size", value: "Contact for details" },
    { id: "highlight_price", icon: "CurrencyRupeeIcon", label: "Price", value: "Contact for quote" },
    { id: "highlight_location", icon: "MapPinIcon", label: "Location", value: "Joka, Kolkata" }]

  },
  {
    id: "jokametro",
    slug: null,
    title: "Near Joka Metro",
    location: "Joka, Kolkata",
    type: "Land for Sale",
    status: "Prime Location",
    description: "Strategically located plot near Joka Metro station. Perfect for families looking to build homes with excellent public transport connectivity. Growing locality with modern amenities.",
    images: [
    {
      id: "img_3",
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_12af060c8-1769245459376.png",
      alt: "Land plot near Joka Metro station with metro visible in background"
    },
    {
      id: "img_4",
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_19838ed01-1770029781755.png",
      alt: "Joka Metro area showing urban development and infrastructure"
    }],

    features: [
    "Walking distance to Metro",
    "High appreciation potential",
    "Modern infrastructure",
    "Commercial hub nearby",
    "24/7 security area"],

    highlights: [
    { id: "highlight_size_2", icon: "MapIcon", label: "Plot Size", value: "Contact for details" },
    { id: "highlight_price_2", icon: "CurrencyRupeeIcon", label: "Price", value: "Contact for quote" },
    { id: "highlight_location_2", icon: "MapPinIcon", label: "Location", value: "Near Joka Metro" }]

  },
  {
    id: "saradagarden",
    slug: "/projects/sarada-garden",
    title: "Sarada Garden",
    location: "Near Pailan, Kolkata",
    type: "Land for Sale",
    status: "Available Now",
    description: "Premium residential plot in Sarada Garden, strategically located near Pailan. Ideal for building your dream home in a peaceful environment with excellent connectivity to major areas of Kolkata.",
    images: [
    {
      id: "img_5",
      src: "/assets/images/residential_plot-for-sale-thakurpukur-Kolkata-plot_view-1769976743121.jpg",
      alt: "Sarada Garden residential plot view near Pailan, Kolkata"
    },
    {
      id: "img_6",
      src: "/assets/images/Screenshot_2026-02-02_014513-1769976926140.png",
      alt: "Sarada Garden detailed plot layout showing land division plan with plot sizes"
    }],

    features: [
    "Prime location near Pailan",
    "Clear property documentation",
    "Good road connectivity",
    "Peaceful residential area",
    "Easy access to amenities"],

    highlights: [
    { id: "highlight_size_3", icon: "MapIcon", label: "Plot Size", value: "Contact for details" },
    { id: "highlight_price_3", icon: "CurrencyRupeeIcon", label: "Price", value: "Contact for quote" },
    { id: "highlight_location_3", icon: "MapPinIcon", label: "Location", value: "Near Pailan, Kolkata" }]

  }];


  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="space-y-16">
          {projects.map((project) =>
          <div
            key={project.id}
            id={project.id}
            className="scroll-mt-24 p-8 md:p-12 bg-muted rounded-3xl border border-border">

              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Images */}
                <div className="space-y-6">
                  <div 
                    className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-white group cursor-pointer"
                    onClick={() => setPopupImage({ src: project.images[0].src, alt: project.images[0].alt })}
                  >
                    <AppImage
                      src={project.images[0].src}
                      alt={project.images[0].alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
                        <Icon name="MagnifyingGlassPlusIcon" size={32} className="text-primary" />
                      </div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="px-4 py-2 bg-secondary/90 backdrop-blur-sm text-secondary-foreground rounded-full text-xs font-bold uppercase tracking-wider">
                        {project.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {project.images.slice(1).map((img) => (
                      <div 
                        key={img.id} 
                        className="relative aspect-[4/3] rounded-xl overflow-hidden bg-white group cursor-pointer"
                        onClick={() => setPopupImage({ src: img.src, alt: img.alt })}
                      >
                        <AppImage
                          src={img.src}
                          alt={img.alt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                            <Icon name="MagnifyingGlassPlusIcon" size={24} className="text-primary" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-8">
                  <div>
                    <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-3">
                      {project.title}
                    </h2>
                    <p className="text-lg text-muted-foreground flex items-center gap-2 mb-2">
                      <Icon name="MapPinIcon" size={20} />
                      {project.location}
                    </p>
                    <p className="text-sm uppercase tracking-wider text-primary font-bold">
                      {project.type}
                    </p>
                  </div>

                  <p className="text-muted-foreground leading-relaxed">
                    {project.description}
                  </p>

                  <div className="grid grid-cols-3 gap-4">
                    {project.highlights.map((highlight) =>
                  <div key={highlight.id} className="p-4 bg-white rounded-lg text-center">
                        <Icon name={highlight.icon as any} size={24} className="text-primary mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground mb-1">{highlight.label}</p>
                        <p className="text-sm font-bold text-foreground">{highlight.value}</p>
                      </div>
                  )}
                  </div>

                  <div>
                    <h3 className="font-bold text-xl text-foreground mb-4">Key Features</h3>
                    <ul className="space-y-3">
                      {project.features.map((feature, index) =>
                    <li key={index} className="flex items-center gap-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-primary shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                    )}
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <Link
                    href={`/contact?project=${project.id}`}
                    className="flex-1 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-center hover:bg-accent transition-all hover:scale-105 flex items-center justify-center gap-2">

                      <Icon name="ChatBubbleLeftRightIcon" size={20} />
                      Enquire About This Plot
                    </Link>
                    <a
                    href="tel:+919088998873"
                    className="flex-1 px-8 py-4 bg-white border-2 border-primary text-primary rounded-lg font-bold text-center hover:bg-primary hover:text-white transition-all flex items-center justify-center gap-2">

                      <Icon name="PhoneIcon" size={20} />
                      Call Now
                    </a>
                  </div>
                  {project.slug && (
                    <div className="pt-2">
                      <Link
                        href={project.slug}
                        className="w-full px-8 py-4 bg-muted border-2 border-border text-foreground rounded-lg font-bold text-center hover:border-primary hover:text-primary transition-all flex items-center justify-center gap-2"
                      >
                        <Icon name="ArrowRightIcon" size={20} />
                        View Full Details
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-16 text-center p-8 bg-muted rounded-2xl">
          <p className="text-lg text-muted-foreground mb-2">
            All projects are within Kolkata city limits
          </p>
          <p className="text-sm text-muted-foreground">
            Contact us for site visits, documentation details, and pricing information
          </p>
        </div>
      </div>

      {popupImage && (
        <ImagePopup
          src={popupImage.src}
          alt={popupImage.alt}
          onClose={() => setPopupImage(null)}
        />
      )}
    </section>);

};

export default ProjectDetailsSection;